// This file is part of InvenioRDM
// Copyright (C) 2020-2024 CERN.
// Copyright (C) 2020-2021 Northwestern University.
// Copyright (C) 2021 Graz University of Technology.
//
// Invenio RDM Records is free software; you can redistribute it and/or modify it
// under the terms of the MIT License; see LICENSE file for more details.

// Overrides to:
// - add popup on Edit and New Version buttons, and accordingly to the published record having a draft version or not.
// - hide the record managment buttons if not owner and not the view of the request preview.

import { i18next } from "@translations/invenio_app_rdm/i18next";

import React, { Component } from "react";
import { Button, Grid, Icon, Message, Popup } from "semantic-ui-react";

import { EditButton } from "@js/invenio_app_rdm/landing_page/EditButton";
import { ShareButton } from "@js/invenio_app_rdm/landing_page/ShareOptions/ShareButton";
import { NewVersionButton } from "@js/invenio_rdm_records";
import PropTypes from "prop-types";
import { ManageButton } from "@js/invenio_app_rdm/landing_page/ManageButton";

export class RecordManagement extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasDraft: null,
      loadingDraft: true,
      error: "",
      popup: {"edit": false, "new_version": false},
    };
  }

  // Check if the published record has a draft
  checkDraft = () => {
    const { record } = this.props;

    fetch(record.links.draft)
    .then((response) => {
      const hasDraft = response.status === 200;
      this.setState({ hasDraft });
      return response.json();
    })
    .then(() => {
      this.setState({
        loadingDraft: false,
      });
    })
    .catch(() => {
      this.setState({ hasDraft: false, loadingDraft: false });
    });
  };

  componentDidMount() {
    this.checkDraft();
  }

  render() {
    const {
      record,
      permissions,
      isDraft,
      isPreviewSubmissionRequest,
      currentUserId,
      recordOwnerID,
      groupsEnabled,
    } = this.props;

    const { hasDraft, loadingDraft, error, popup } = this.state;
    const { id: recid } = record;

    // Hide the record-management buttons if the user is not the owner and if it is not the view of the request preview.
    if (this.props.currentUserId !== record.parent.access.owned_by.user && !isPreviewSubmissionRequest) {
      document.getElementById("record-manage-menu")?.style.setProperty("display", "none");
    }

    const handleError = (errorMessage) => {
      this.setState({ error: errorMessage });
    };

    const handlePopup = (event, action) => {
      event.persist();
      if (event.type === "mouseenter") {
        if (action === "edit") {
          this.setState({ popup: {"edit": true, "new_version": false} });
        } else {
          this.setState({ popup: {"edit": false, "new_version": true} });
        }
      }
      if (event.type === "mouseleave") this.setState({ popup: {"edit": false, "new_version": false} });
    };

    const popupMessageEdit= "\
    By clicking the Edit button, a \"Published version draft\" will be created in \"My records\".\
    Only keywords, funding, references or Materials Cloud sections using these data can be edited.\
    "
    // const popupMessageNoEdit= "\
    // You cannot edit this version because a \"Published version draft\" or a \"New version draft\" exists. Look in \"My records\".\
    // "
    const popupMessageNewVersion= "\
    By clicking the New version button, a \"New version draft\" will be created in \"My records\".\
    "
    // const popupMessageNoNewVersion = "\
    // You cannot create a new version because a \"Published version draft\" or a \"New version draft\" exists. Look in \"My records\".\
    // "

    if (loadingDraft) {
      return <div></div>;
    }

    return (
      <Grid columns={1} className="record-management">
        {/* Messages on top of buttons */}
        {permissions.can_edit && permissions.can_new_version &&
        !isPreviewSubmissionRequest && (
          <Grid.Column>
            <Message info className="no-border-radius m-0">
              This record is published. You can either Edit this version or create a New version.
            </Message>
          </Grid.Column>
        )}

        {permissions.can_edit && hasDraft &&
        !isPreviewSubmissionRequest && (
          <Grid.Column>
            <Message info className="no-border-radius m-0">
              This record is published. A draft of this version exists. Click Edit to publish or discard the changes.
            </Message>
          </Grid.Column>
        )}

        {/* owners */}
        {!permissions.can_edit &&
        !isPreviewSubmissionRequest && (
          <Grid.Column>
            <Message info className="no-border-radius m-0">
              This record is published. You cannot edit this version because a new version draft or a draft of another version exists. Look in <a href="/me/uploads?q=&f=allversions%3Atrue&l=list&p=1&s=10&sort=updated-desc">My records</a>.
            </Message>
          </Grid.Column>
        )}

        {/* not owner and not moderator, users accessing via secret links.
        Only the owner can create a published or new version draft.
        Even if the draft does not exist the Edit and New version buttons return Permission denied.*/}
        {/* {!permissions.can_edit && !permissions.can_manage &&
        !isPreviewSubmissionRequest && (
          <Grid.Column>
            <Message info className="no-border-radius m-0">
              This record is published. You do not have permissions to edit this version or create a new version.
            </Message>
          </Grid.Column>
        )} */}

        {/* Admin button (disabled by permissions) */}
        {permissions.can_moderate && (
          <Grid.Column className="pb-5">
            <ManageButton recid={recid} recordOwnerID={recordOwnerID} />
          </Grid.Column>
        )}

        {/* ----------- */}
        {/* Edit button */}
        {/* ----------- */}

        {/* Case can edit */}
        {permissions.can_edit && !isDraft && (
          <Grid.Column className={permissions.can_manage ? "pb-5 pt-5" : "pb-5"}>
            {!hasDraft ? (
              <Popup
                role="alert"
                open={popup["edit"]}
                content={popupMessageEdit}
                inverted={true}
                position={"top center"}
                size="small"
                trigger={
                  <p
                    onMouseEnter={(e) => handlePopup(e, "edit")} // Show popup on mouse over
                    onMouseLeave={(e) => handlePopup(e, "edit")}   // Hide popup on mouse out
                  >
                    <EditButton recid={recid} onError={handleError}/>
                  </p>
                }
              />
              ) : (
                <EditButton recid={recid} onError={handleError}/>
              )}
          </Grid.Column>
        )}

        {/* Case cannot edit */}
        {!permissions.can_edit && !isPreviewSubmissionRequest && (
          <></>
          // <Grid.Column className={permissions.can_manage ? "pb-5 pt-5" : "pb-5"}>
          //   <Popup
          //     role="alert"
          //     open={popup["edit"]}
          //     content={popupMessageNoEdit}
          //     inverted={true}
          //     position={"top center"}
          //     size="small"
          //     trigger={
          //     <p
          //       onMouseEnter={(e) => handlePopup(e, "edit")} // Show popup on mouse over
          //       onMouseLeave={(e) => handlePopup(e, "edit")}   // Hide popup on mouse out
          //     >
          //       <EditButton recid={recid} onError={handleError}/>
          //     </p>
          //     }
          //   />
          // </Grid.Column>
        )}

        {/* Edit button in requests */}
        {isPreviewSubmissionRequest && isDraft && (
          <Grid.Column className="pb-20">
            <Button
              fluid
              className="warning"
              size="medium"
              onClick={() => (window.location = `/uploads/${recid}`)}
              icon
              labelPosition="left"
            >
              <Icon name="edit" />
              {i18next.t("Edit")}
            </Button>
          </Grid.Column>
        )}

        {/* ------------------ */}
        {/* New version button */}
        {/* ------------------ */}

        {/* Case can create new version */}
        {permissions.can_new_version && !isPreviewSubmissionRequest && (
          <Grid.Column className="pt-5 pb-5">
            <Popup
            role="alert"
            open={popup["new_version"]}
            content={popupMessageNewVersion}
            inverted={true}
            position={"top center"}
            size="small"
            trigger={
            <p
              onMouseEnter={handlePopup} // Show popup on mouse over
              onMouseLeave={handlePopup}   // Hide popup on mouse out
            >
              <NewVersionButton
              fluid
              size="medium"
              record={record}
              onError={handleError}
              disabled={!permissions.can_new_version}
              />
            </p>
            }
          />
          </Grid.Column>
        )}

        {/* Case cannot create new version */}
        {!permissions.can_new_version && !isPreviewSubmissionRequest && (
          <></>
          // <Grid.Column className="pt-5 pb-5">
          //   <Popup
          //   role="alert"
          //   open={popup["new_version"]}
          //   content={popupMessageNoNewVersion}
          //   inverted={true}
          //   position={"top center"}
          //   size="small"
          //   trigger={
          //   <p
          //     onMouseEnter={handlePopup} // Show popup on mouse over
          //     onMouseLeave={handlePopup}   // Hide popup on mouse out
          //   >
          //     <NewVersionButton
          //     fluid
          //     size="medium"
          //     record={record}
          //     onError={handleError}
          //     />
          //   </p>
          //   }
          // />
          // </Grid.Column>
        )}

        {/* ------------ */}
        {/* Share button */}
        {/* ------------ */}

        {/* Share button on published record */}
        {/*permissions.can_manage && !isPreviewSubmissionRequest && (
          <Grid.Column className="pt-5">
            <ShareButton
              disabled={!permissions.can_update_draft}
              record={record}
              permissions={permissions}
              groupsEnabled={groupsEnabled}
            />
          </Grid.Column>
        )*/}

        {error && (
          <Grid.Row className="record-management">
            <Grid.Column>
              <Message negative>{error}</Message>
            </Grid.Column>
          </Grid.Row>
        )}
      </Grid>
    );
  }
}

RecordManagement.propTypes = {
  record: PropTypes.object.isRequired,
  permissions: PropTypes.object.isRequired,
  isDraft: PropTypes.bool.isRequired,
  groupsEnabled: PropTypes.bool.isRequired,
  isPreviewSubmissionRequest: PropTypes.bool.isRequired,
  currentUserId: PropTypes.string.isRequired,
  recordOwnerID: PropTypes.string.isRequired,
};
